/**
 * Meta (Facebook) Pixel — site-wide install, pixel ID 1728861491525973.
 *
 * Mirrors Meta's own base code (Events Manager > Data Sources > this pixel >
 * Settings > Set up manually) line for line, just split so nothing needs to
 * run as an inline <script> in index.html: the Content-Security-Policy has
 * no 'unsafe-inline' in script-src, and that is load-bearing (see the CSP
 * comment in index.html). This module is bundled into the app's own script,
 * which CSP already trusts via 'self'; only the actual fbevents.js file is
 * fetched from Meta, from https://connect.facebook.net, which script-src
 * allow-lists for exactly that one file. Events are posted to
 * https://www.facebook.com, allow-listed in connect-src.
 *
 * The queueing stub below is installed the moment this module is imported
 * (module evaluation, not a useEffect) so `fbq(...)` is always safe to call
 * from any page, before or after fbevents.js has actually finished loading —
 * calls made early just queue and get replayed once it's ready. That is the
 * same trick Meta's own snippet uses (the stub is defined inline, synchronously,
 * before the real script tag is even inserted); this only moves it out of
 * inline HTML and into a module so CSP doesn't need weakening to run it.
 */

type Fbq = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: Fbq;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

export const META_PIXEL_ID = "1728861491525973";

const PIXEL_SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";

function installStub(): void {
  if (typeof window === "undefined" || window.fbq) return;

  const fbq = function (...args: unknown[]) {
    const method = fbq.callMethod;
    if (method) {
      method.apply(fbq, args);
    } else {
      fbq.queue.push(args);
    }
  } as Fbq;

  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];

  window.fbq = fbq;
  window._fbq = fbq;
}

installStub();

let scriptRequested = false;

function loadPixelScript(): void {
  if (scriptRequested || typeof document === "undefined") return;
  scriptRequested = true;

  if (document.querySelector(`script[src="${PIXEL_SCRIPT_SRC}"]`)) return;

  const script = document.createElement("script");
  script.async = true;
  script.src = PIXEL_SCRIPT_SRC;

  // Same placement Meta's own snippet uses (insert before the first existing
  // script). Falls back to <head> when there is no earlier script to anchor
  // on — always true in production (index.html loads main.tsx first), but
  // not guaranteed in a bare test DOM.
  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    (document.head ?? document.body)?.appendChild(script);
  }
}

let initialized = false;

/** Loads fbevents.js and calls fbq('init', ...). Safe to call more than once. */
export function initMetaPixel(): void {
  if (initialized) return;
  initialized = true;
  loadPixelScript();
  window.fbq?.("init", META_PIXEL_ID);
}

/** Fires a pixel event. Safe to call even before fbevents.js has finished loading. */
export function trackPixelEvent(eventName: string, params?: Record<string, unknown>): void {
  if (params) {
    window.fbq?.("track", eventName, params);
  } else {
    window.fbq?.("track", eventName);
  }
}

/** Test-only: resets module-level state so each test starts clean. */
export function __resetMetaPixelForTests(): void {
  initialized = false;
  scriptRequested = false;
  delete window.fbq;
  delete window._fbq;
}
